import { useEffect, useRef, useState } from 'react';
import { MdClearAll } from 'react-icons/md';

import { useAppConfig } from '../../../context/AppConfigContext.jsx';
import { useAppContext } from '../../../context/appContext.jsx';
import { useAuthContext } from '../../../context/AuthContext.jsx';
import { useBackendStatusContext } from '../../../context/backendStatus/backendStatusContext.jsx';
import { SET_BACKEND_STATUS_REBOOTING } from '../../../context/backendStatus/backendStatusActions';
import { useCognitoContext } from '../../../context/CognitoContext.jsx';
import { useError } from '../../../context/errorContext.jsx';
import { usePollingManager } from '../../../context/PollingManagerContext.jsx';
import { useSidebar } from '../../../context/SidebarContext.jsx';
import { useTranslation } from '../../../context/TranslationContext.jsx';
import logError from '../../../utils/logError';
import logger from '../../../utils/logger';
import { formatName } from '../../../utils/formatName';
import {
  DarkModeToggle,
  HeaderOverflowMenu,
  LanguageSelector,
  SoundControl,
} from '../../HeaderControls';
import UpdateButton from '../../UpdateButton.jsx';

import DeviceActionsDialog from './DeviceActionsDialog.jsx';
import HeaderUserMenu from './HeaderUserMenu.jsx';
import {
  HeaderActions,
  HeaderAppBar,
  HeaderControlsTray,
  HeaderSidebarButton,
  HeaderSpacer,
  HeaderToolbar,
} from './styles';

const COMPACT_HEADER_WIDTH = 940;

const Header = () => {
  const { translate } = useTranslation();
  const { signOut } = useCognitoContext();
  const { userData } = useAuthContext();
  const {
    toggleCompatibility,
    isLocalHost,
    toggleMockTest,
    togglePhoneBypass,
    isPhoneBypassed,
    isTestMocked,
  } = useAppContext();
  const { toggleSidebar } = useSidebar();
  const backendStatus = useBackendStatusContext();
  const { runningState } = usePollingManager();
  const { handleError } = useError();
  const { api } = useAppConfig();
  const [isRebootDialogOpen, setIsRebootDialogOpen] = useState(false);
  const [isShutdownDialogOpen, setIsShutdownDialogOpen] = useState(false);
  const [loadingReboot, setLoadingReboot] = useState(false);
  const [loadingShutdown, setLoadingShutdown] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [headerWidth, setHeaderWidth] = useState(0);
  const [isUserCardOpen, setIsUserCardOpen] = useState(false);
  const headerRef = useRef(null);
  const avatarButtonRef = useRef(null);

  const backendIsRebooting = backendStatus.state.status === 'rebooting';
  const isRebooting = localStorage.getItem('sonda.backend.rebooting') === 'true';
  const isCompactHeader = headerWidth > 0 && headerWidth < COMPACT_HEADER_WIDTH;
  const showUpdateButton = backendStatus.state.isUpdatePossible;
  const isTestNotFailedAndNotReported = runningState?.isTestNotFailedAndNotReported ?? true;
  const currentUser = formatName(userData.firstName, userData.lastName);

  useEffect(() => {
    if (!backendIsRebooting && !isRebooting) {
      setIsRebootDialogOpen(false);
      setLoadingReboot(false);
    }
  }, [backendIsRebooting, isRebooting]);

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return undefined;

    const updateHeaderWidth = () => {
      setHeaderWidth(headerElement.getBoundingClientRect().width);
    };

    updateHeaderWidth();

    const resizeObserver = new ResizeObserver(updateHeaderWidth);
    resizeObserver.observe(headerElement);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!isModalClosing) return;

    if (!loadingShutdown) {
      setIsShutdownDialogOpen(false);
      setLoadingShutdown(false);
    }

    if (!isRebooting && !loadingShutdown) {
      setIsModalClosing(false);
    }
  }, [isModalClosing, loadingShutdown, isRebooting]);

  const sendDeviceCommand = async (endpoint, { action, onStart, signOutAfter = false } = {}) => {
    try {
      if (!api.acquire.baseUrl) {
        throw new Error('Missing acquire base URL');
      }

      onStart?.();
      const response = await fetch(`${api.acquire.baseUrl}/${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': localStorage.getItem('csrfToken'),
        },
      });

      if (!response.ok) {
        throw new Error(`${endpoint} request failed with status ${response.status}`);
      }

      logger.info(`${endpoint} request sent successfully`);
      if (signOutAfter) signOut();
      return true;
    } catch (error) {
      logger.error(logError(error, { action }));
      handleError(error);
      return false;
    } finally {
      setIsModalClosing(true);
    }
  };

  const handleReboot = async () => {
    const succeeded = await sendDeviceCommand('reboot', {
      action: 'sendRebootRequest',
      onStart: () => setLoadingReboot(true),
    });

    if (succeeded) {
      localStorage.setItem('sonda.backend.rebooting', 'true');
      backendStatus.dispatch({ type: SET_BACKEND_STATUS_REBOOTING });
    } else {
      setLoadingReboot(false);
      setIsRebootDialogOpen(false);
    }
  };

  const handleShutdown = async () => {
    const succeeded = await sendDeviceCommand('shutdown', {
      action: 'sendShutdownRequest',
      onStart: () => setLoadingShutdown(true),
      signOutAfter: true,
    });

    if (!succeeded) setLoadingShutdown(false);
  };

  const handleSidebarControlButton = (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleSidebar();
  };

  const localHostToggles = [
    {
      key: 'compatibility',
      label: translate('header.BypassCompatibility'),
      onChange: (event) => {
        toggleCompatibility(event.target.checked);
        location.reload();
      },
      defaultChecked: JSON.parse(localStorage.getItem('compatibility')),
    },
    {
      key: 'mockTest',
      label: translate('header.MockDataAcquisition'),
      onChange: (event) => toggleMockTest(event.target.checked),
      defaultChecked: JSON.parse(isTestMocked),
    },
    {
      key: 'phoneBypass',
      label: translate('header.MockPhoneDetection'),
      onChange: (event) => togglePhoneBypass(event.target.checked),
      defaultChecked: JSON.parse(isPhoneBypassed),
    },
  ];

  const userMenu = (
    <HeaderUserMenu
      avatarButtonRef={avatarButtonRef}
      currentUser={currentUser}
      isOpen={isUserCardOpen}
      isLocalHost={isLocalHost}
      localHostToggles={localHostToggles}
      onOpenRebootDialog={() => setIsRebootDialogOpen(true)}
      onOpenShutdownDialog={() => setIsShutdownDialogOpen(true)}
      onSignOut={signOut}
      onToggle={() => setIsUserCardOpen((previousValue) => !previousValue)}
    />
  );

  return (
    <>
      <HeaderAppBar ref={headerRef} position="static" elevation={0}>
        <HeaderToolbar>
          <HeaderSidebarButton
            onClick={handleSidebarControlButton}
            aria-label={translate('header.toggleSidebar')}
          >
            <MdClearAll size={25} />
          </HeaderSidebarButton>
          <HeaderSpacer />
          <HeaderActions>
            {showUpdateButton ? <UpdateButton /> : null}
            {!isCompactHeader ? (
              <HeaderControlsTray>
                <DarkModeToggle variant="pill" />
                <SoundControl variant="pill" />
                <LanguageSelector variant="pill" />
                {userMenu}
              </HeaderControlsTray>
            ) : (
              <>
                <HeaderOverflowMenu />
                {userMenu}
              </>
            )}
          </HeaderActions>
        </HeaderToolbar>
      </HeaderAppBar>

      <DeviceActionsDialog
        backendIsRebooting={backendIsRebooting}
        isRebootDialogOpen={isRebootDialogOpen}
        isRebooting={isRebooting}
        isShutdownDialogOpen={isShutdownDialogOpen}
        isTestNotFailedAndNotReported={isTestNotFailedAndNotReported}
        loadingReboot={loadingReboot}
        loadingShutdown={loadingShutdown}
        onCloseReboot={() => {
          setIsRebootDialogOpen(false);
          setIsModalClosing(true);
        }}
        onCloseShutdown={() => {
          setIsShutdownDialogOpen(false);
          setIsModalClosing(true);
        }}
        onReboot={handleReboot}
        onShutdown={handleShutdown}
      />
    </>
  );
};

export default Header;
